import { UIRouterGlobals } from "@uirouter/angular";
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, Observable, Subscription, debounceTime, distinctUntilChanged } from "rxjs";
import { IdMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { CommentsScrollToEditorEvent } from "@webapp/comments/models/comments.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { CheckInTemplate } from "@webapp/reflections/models/checkins-models";
import { ReflectionEdit, ReflectionPanel, ReflectionPanelSnapshot, ReflectionPublish } from "@webapp/reflections/models/reflection-panel.models";
import { ReflectionAnswerChange, ReflectionMoodChange, ReflectionNoteChange, ReflectionOrder } from "@webapp/reflections/models/reflection-question.model";
import {
  CheckinCommentChange,
  CheckinCommentPendingTextChange,
  CheckinCommentsToggle,
  DEFAULT_CADENCE_DURATION_IN_DAYS,
  ReflectionOverviewFilterName,
} from "@webapp/reflections/models/reflections.models";
import { LabelsFilterPipe } from "@webapp/reflections/pipes/labels.pipe";
import {
  buildCheckinCommentsCounterAriaLabel,
  generateCommentTemplate,
  getFilteredByNoReflectionLength,
  getFilteredByNoteTypeLength,
  getFilteredByReflectionLength,
  getLastEditableMondayAsDate,
  getLatestDateFromComments,
  isNoteFilterEnabled,
} from "@webapp/reflections/utils";
import { isReflectionEmpty } from "@webapp/reflections/utils/is-reflection-empty";
import dayjs from "@webapp/shared/libs/dayjs";
import { WidthResizeObservable } from "@webapp/ui/core/core.utils";

@UntilDestroy()
@Component({
  selector: "reflection-orchestrator",
  templateUrl: "./reflection-orchestrator.component.html",
  styleUrls: ["./reflection-orchestrator.component.less"],
  providers: [LabelsFilterPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionOrchestratorComponent implements OnChanges, OnInit, OnDestroy, AfterViewChecked {
  @Input() public currReflections: ReflectionPanel[];
  @Input() public prevReflections: ReflectionPanel[];
  @Input() public compareMode = false;
  @Input() public filter: ReflectionOverviewFilterName;
  @Input() public expanedPanelId: string;
  @Input() public currCadenceStartDate: string;
  @Input() public prevCadenceStartDate: string;
  @Input() public checkInTemplate: CheckInTemplate;
  @Input() public currentPage: number;
  @Input() public pageSize: number;
  @Input() public expandCollapseAll: Observable<boolean>;
  @Input() public assignees: IdMap<Assignee>;
  @Output() public readonly reflectionPublish = new EventEmitter<ReflectionPublish>();
  @Output() public readonly reflectionEdit = new EventEmitter<ReflectionEdit>();
  @Output() public readonly pageChange = new EventEmitter<number>();

  public maxReflectionsLength: number;
  private reflectionSnapshot: ReflectionPanelSnapshot = { current: {} as ReflectionPanel, previous: {} as ReflectionPanel };
  public isReflectionEmpty = isReflectionEmpty;
  private shouldScrollIntoView = false;
  private expandCollapseAllSub: Subscription;
  public currCheckInsModified: ReflectionPanel[];
  public prevCheckInsModified: ReflectionPanel[];
  public isCheckInsUxImprovementsEnabled: boolean;
  public isLeftNavigationEnabled: boolean;
  public maxVisibleLabels = 2;
  private currentBrowserWidth: number;
  public scrollToCommentsEditor$: BehaviorSubject<CommentsScrollToEditorEvent> = new BehaviorSubject<CommentsScrollToEditorEvent>(null);
  public generateCommentTemplate = generateCommentTemplate;
  public getLatestDateFromComments = getLatestDateFromComments;
  public isNoteFilterEnabled = isNoteFilterEnabled;
  public scrollToComment: string;
  public buildCheckinCommentsCounterAriaLabel = buildCheckinCommentsCounterAriaLabel;

  public collapsePanelHeaderStyles: Record<string, string> = {
    padding: "8px 24px",
  };

  constructor(
    featureToggleFacade: FeatureTogglesFacade,
    private isNavExpanded: IsNavigationExpandedCache,
    private cdr: ChangeDetectorRef,
    private routerGlobals: UIRouterGlobals,
    private labelsFilterPipe: LabelsFilterPipe,
    private elementRef: ElementRef<HTMLElement>
  ) {
    featureToggleFacade
      .isFeatureAvailable$(FeatureFlag.CheckInsUxImprovements)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (enabled) => {
          this.isCheckInsUxImprovementsEnabled = enabled;
        },
      });
  }

  public getShouldScrollIntoView(): boolean {
    return this.shouldScrollIntoView;
  }

  public ngOnInit(): void {
    if (this.routerGlobals.params["expandCheckIns"]) {
      this.updateAllPanelsExpandedCollapsedState();
    }

    if (this.filter === "published") {
      this.maxReflectionsLength = this.currReflections.filter((reflection) => !reflection.noReflection).length;
    } else {
      this.maxReflectionsLength = this.currReflections.length;
    }

    this.expandCollapseAllSub = this.expandCollapseAll.subscribe((state) => this.toggleExpandCollapseAll(state));
    this.currentBrowserWidth = window.innerWidth;
    this.updateVisibleLabels();

    this.isNavExpanded.get$().subscribe((value) => {
      this.isLeftNavigationEnabled = value;
      this.updateVisibleLabels();
    });

    new WidthResizeObservable(document.body).pipe(debounceTime(200), distinctUntilChanged(), untilDestroyed(this)).subscribe((width) => {
      this.currentBrowserWidth = width;
      this.updateVisibleLabels();
    });
  }

  public ngOnDestroy(): void {
    if (this.expandCollapseAllSub) {
      this.expandCollapseAllSub.unsubscribe();
      this.expandCollapseAllSub = null;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.currReflections || changes.prevReflections || changes.filter || changes.compareMode) {
      if (!this.filter) {
        this.maxReflectionsLength = this.calculateNonFilteredMaxReflectionsLength();
      } else if (this.compareMode) {
        this.maxReflectionsLength = this.calculateFilteredMaxReflectionsLengthInCompareMode();
      } else {
        this.maxReflectionsLength = this.calculateFilteredMaxReflectionsLengthInNonCompareMode();
      }
    }

    const isFilterChanged = changes.filter?.firstChange === false && changes.filter.currentValue !== changes.filter.previousValue;
    const isCurrentPageChanged = changes.currentPage?.firstChange === false && changes.currentPage.currentValue !== changes.currentPage.previousValue;
    if (isFilterChanged || isCurrentPageChanged) {
      this.updateAllPanelsExpandedCollapsedState();
    }

    this.currCheckInsModified = this.sliceCheckInsPage(this.filterCheckIns(this.currReflections, this.prevReflections));
    this.prevCheckInsModified = this.sliceCheckInsPage(this.filterCheckIns(this.prevReflections, this.currReflections));

    if (changes.expanedPanelId) {
      this.shouldScrollIntoView = true;
    }

    if (changes.compareMode) {
      this.updateVisibleLabels();
    }

    if (changes?.filter?.currentValue === "published" && this.routerGlobals.params["commentId"]) {
      this.scrollToComment = this.routerGlobals.params["commentId"];
    }
  }

  public ngAfterViewChecked(): void {
    if (this.shouldScrollIntoView) {
      const element = this.elementRef.nativeElement.querySelector(`#check-in-${this.expanedPanelId}`);
      if (element) {
        window.setTimeoutOutsideAngular(() => {
          element.scrollIntoView({ behavior: "smooth" });
        });
        this.shouldScrollIntoView = false;
      }
    }
  }

  public onMoodUpdate(data: ReflectionMoodChange, index: number): void {
    const selectedReflections = data.type === "current" ? this.currReflections : this.prevReflections;
    if (selectedReflections[index].mood === data.mood) {
      selectedReflections[index].mood = undefined;
    } else {
      selectedReflections[index].mood = data.mood;
    }
    this.reflectionEdit.emit({ checkin: selectedReflections[index] });
  }

  public onNoteUpdate(data: ReflectionNoteChange, index: number): void {
    const selectedReflections = data.type === "current" ? this.currReflections : this.prevReflections;
    selectedReflections[index].notes[data.noteIndex].text = data.note.text;

    if (data.note?.gif?.id) {
      selectedReflections[index].notes[data.noteIndex].gif = data.note.gif;
    } else {
      selectedReflections[index].notes[data.noteIndex].gif = { id: "" };
    }

    this.reflectionEdit.emit({ checkin: selectedReflections[index] });
  }

  public onAnswerUpdate(data: ReflectionAnswerChange, index: number): void {
    const selectedReflections = data.type === "current" ? this.currReflections : this.prevReflections;
    const answerToUpdateIndex = selectedReflections[index].questions?.findIndex((obj) => obj.id == data.question.id);
    if (answerToUpdateIndex !== undefined) {
      selectedReflections[index].questions[answerToUpdateIndex].answer = data.question.answer;

      if (data.question?.gif?.id) {
        selectedReflections[index].questions[answerToUpdateIndex].gif = data.question.gif;
      } else {
        selectedReflections[index].questions[answerToUpdateIndex].gif = { id: "" };
      }

      this.reflectionEdit.emit({ checkin: selectedReflections[index] });
    }
  }

  public onFillOut(type: ReflectionOrder, index: number): void {
    const selectedReflections = type === "current" ? this.currReflections : this.prevReflections;
    selectedReflections[index].noReflection = false;
    selectedReflections[index].isEditing = true;
    this.reflectionEdit.emit({ checkin: selectedReflections[index] });
  }

  public onEditReflection(type: ReflectionOrder, index: number): void {
    const selectedReflections = type === "current" ? this.currReflections : this.prevReflections;
    selectedReflections[index].isEditing = true;
    this.reflectionSnapshot[type] = structuredClone(selectedReflections[index]);
    this.reflectionEdit.emit({ checkin: selectedReflections[index] });
    // Fix mood component to be focused after the user clicks on Check-in edit button
    window.setTimeoutOutsideAngular(() => {
      const component = this.elementRef.nativeElement.querySelector(`.reflection-mood-${type}`);
      const title = component.querySelector<HTMLElement>(".reflection-mood-title");

      if (component && title) {
        component.scrollIntoView({ block: "nearest" });
        title.focus();
      }
    });
  }

  public onPublishReflection(type: ReflectionOrder, index: number): void {
    const selectedReflection = type === "current" ? this.currReflections[index] : this.prevReflections[index];

    selectedReflection.isEditing = false;
    selectedReflection.indicators.loadingIndicator = { progress: true };
    this.reflectionPublish.emit({ checkin: selectedReflection, type });
  }

  public onCancelReflection(type: ReflectionOrder, index: number): void {
    const canceledReflectionState: Partial<ReflectionPanel> = {
      isEditing: false,
      questions: this.reflectionSnapshot[type].questions,
      notes: this.reflectionSnapshot[type].notes,
      mood: this.reflectionSnapshot[type].mood,
      noReflection: this.reflectionSnapshot[type].noReflection,
    };

    if (type === "current") {
      this.currReflections[index] = { ...this.currReflections[index], ...canceledReflectionState };
      this.reflectionEdit.emit({ checkin: this.currReflections[index], editCancel: true });
    } else {
      this.prevReflections[index] = { ...this.prevReflections[index], ...canceledReflectionState };
      this.reflectionEdit.emit({ checkin: this.prevReflections[index], editCancel: true });
    }
  }

  public onToggleComments(data: CheckinCommentsToggle, index: number): void {
    if (data.type === "current") {
      this.toggleComments(this.currReflections, index, data);
    } else {
      this.toggleComments(this.prevReflections, index, data);
    }

    this.scrollToCommentsEditor$.next({ targetId: data.targetId, scrollToEditor: data.scrollToEditor });
    this.scrollToComment = "";
  }

  public onAddComment(data: CheckinCommentChange, index: number): void {
    if (data.type === "current") {
      this.addComment(this.currReflections, index, data);
    } else {
      this.addComment(this.prevReflections, index, data);
    }
  }

  public onRemoveComment(data: CheckinCommentChange, index: number): void {
    if (data.type === "current") {
      this.removeComment(this.currReflections, index, data);
    } else {
      this.removeComment(this.prevReflections, index, data);
    }
  }

  public onChangeCommentPendingText(data: CheckinCommentPendingTextChange, index: number): void {
    if (data.type === "current") {
      this.changeCommentPendingText(this.currReflections[index], data);
    } else {
      this.changeCommentPendingText(this.prevReflections[index], data);
    }
  }

  public trackByMethod(index: number, reflection: ReflectionPanel): string {
    return `${index}-${reflection.id}-${reflection.isActive}-${reflection.createdAt}`;
  }

  public activeChanged(params: { active: boolean }, assigneeId: string): void {
    const selectedReflection = this.currReflections.find((reflection) => reflection.assignee.id === assigneeId);
    selectedReflection.isActive = params.active;

    if (!params.active) {
      this.collapseAllCommentsSections(this.currReflections, assigneeId);

      if (this.prevReflections) {
        this.collapseAllCommentsSections(this.prevReflections, assigneeId);
      }
    }
  }

  public onPageIndexChange(page: number): void {
    this.pageChange.emit(page);
  }

  public isPanelEnabled({
    isCurrReflectionEmpty,
    isPrevReflectionEmpty,
    isEditable,
  }: {
    isCurrReflectionEmpty: boolean;
    isPrevReflectionEmpty: boolean;
    isEditable: boolean;
  }): boolean {
    const prevMondayAsDate = getLastEditableMondayAsDate();

    const isCurrCadenceEditable = dayjs(this.currCadenceStartDate).isSameOrAfter(prevMondayAsDate) && isEditable;

    const shouldDefaultModeBeEnabled = !this.compareMode && (isEditable || !isCurrReflectionEmpty) && this.filter !== "notPublished";
    const shouldCompareModeBeEnabled =
      this.compareMode &&
      ((this.filter && this.filter !== "notPublished") || (!this.filter && (!isPrevReflectionEmpty || !isCurrReflectionEmpty || isCurrCadenceEditable)));

    return shouldDefaultModeBeEnabled || shouldCompareModeBeEnabled;
  }

  // Start cadence from Monday for tracking purposes
  public calculateCurrCadenceStartDate(): string {
    return dayjs(this.currCadenceStartDate).utc().add(1, "day").toISOString();
  }

  public calculateCurrCadenceEndDate(): string {
    return dayjs(this.currCadenceStartDate).utc().add(DEFAULT_CADENCE_DURATION_IN_DAYS, "day").toISOString();
  }

  public buildPanelAriaLabel(checkin: ReflectionPanel): string {
    if (checkin.isCurrentUser && checkin.noReflection) {
      return `${checkin.assignee.name}'s Check-in fill out form`;
    }

    const labels = this.labelsFilterPipe
      .transform(checkin.labels, this.filter)
      .map((label) => ` ${label.type}`)
      .toString();

    if (checkin.noReflection) {
      return `${checkin.assignee.name}'s Check-in,${labels}`;
    }

    if (!checkin.noReflection) {
      if (!checkin.mood && labels.length) {
        return `${checkin.assignee.name}'s Check-in, noted${labels}`;
      }

      if (checkin.mood && !labels.length) {
        return `${checkin.assignee.name}'s Check-in, in ${checkin.mood} mood`;
      }

      if (checkin.mood && labels.length) {
        return `${checkin.assignee.name}'s Check-in, in ${checkin.mood} mood, noted${labels}`;
      }
    }

    return `${checkin.assignee.name}'s Check-in`;
  }

  private filterCheckIns(checkIns: ReflectionPanel[], comparedCheckIns: ReflectionPanel[]): ReflectionPanel[] {
    if (this.filter) {
      if (this.filter === "notPublished") {
        return checkIns.filter((currCheckIn, index) => currCheckIn?.noReflection || (this.compareMode && comparedCheckIns?.[index]?.noReflection));
      }
      if (this.filter === "published") {
        return checkIns.filter((currCheckIn, index) => !currCheckIn?.noReflection || (this.compareMode && !comparedCheckIns?.[index]?.noReflection));
      }
      return checkIns.filter(
        (currCheckIn, index) => this.checkNotes(currCheckIn, this.filter) || (this.compareMode && this.checkNotes(comparedCheckIns?.[index], this.filter))
      );
    }

    return checkIns;
  }

  private sliceCheckInsPage(checkIns: ReflectionPanel[]): ReflectionPanel[] {
    const startIndex = this.pageSize * (this.currentPage - 1);
    const endIndex = startIndex + this.pageSize;

    return checkIns.slice(startIndex, endIndex);
  }

  private checkNotes = (reflection: ReflectionPanel, filter: ReflectionOverviewFilterName): boolean =>
    reflection?.notes.some((note) => note.type === filter && (note.text?.length || note.gif?.id));

  private updateAllPanelsExpandedCollapsedState(): void {
    this.currReflections.forEach((reflection) => {
      const shouldPanelBeActive =
        (this.filter && this.filter !== "notPublished" && this.filter !== "published") ||
        (!this.filter && reflection.isEditable && reflection.noReflection && !!this.expanedPanelId);

      reflection.isActive = shouldPanelBeActive;
    });
  }

  private toggleExpandCollapseAll(state: boolean): void {
    this.currReflections.forEach((reflection, index) => {
      reflection.isActive =
        state &&
        this.isPanelEnabled({
          isCurrReflectionEmpty: reflection.noReflection,
          isPrevReflectionEmpty: this.prevReflections[index]?.noReflection,
          isEditable: reflection.isEditable,
        });
    });
  }

  private calculateNonFilteredMaxReflectionsLength(): number {
    return this.compareMode ? Math.max(this.currReflections.length, this.prevReflections.length) : this.currReflections.length;
  }

  private calculateFilteredMaxReflectionsLengthInCompareMode(): number {
    if (this.filter === "notPublished") {
      const currentLength = getFilteredByNoReflectionLength(this.currReflections);
      const previousLength = getFilteredByNoReflectionLength(this.prevReflections);
      return Math.max(currentLength, previousLength);
    }

    if (this.filter === "published") {
      const currentLength = getFilteredByReflectionLength(this.currReflections);
      const previousLength = getFilteredByReflectionLength(this.prevReflections);
      return Math.max(currentLength, previousLength);
    }

    const currentLength = getFilteredByNoteTypeLength(this.currReflections, this.filter);
    const previousLength = getFilteredByNoteTypeLength(this.prevReflections, this.filter);
    return Math.max(currentLength, previousLength);
  }

  private calculateFilteredMaxReflectionsLengthInNonCompareMode(): number {
    if (this.filter === "notPublished") {
      return getFilteredByNoReflectionLength(this.currReflections);
    }

    if (this.filter === "published") {
      return getFilteredByReflectionLength(this.currReflections);
    }

    return getFilteredByNoteTypeLength(this.currReflections, this.filter);
  }

  private updateVisibleLabels(): void {
    if (this.currentBrowserWidth <= 1280 && this.isLeftNavigationEnabled && !this.compareMode) {
      this.maxVisibleLabels = 1;
    }
    if (this.currentBrowserWidth <= 1280 && this.compareMode) {
      this.maxVisibleLabels = 0;
    }
    if (this.currentBrowserWidth >= 1025 && !this.compareMode && !this.isLeftNavigationEnabled) {
      this.maxVisibleLabels = 2;
    }
    if (this.currentBrowserWidth >= 1281 && !this.compareMode && !this.isLeftNavigationEnabled) {
      this.maxVisibleLabels = 5;
    }
    if (this.currentBrowserWidth >= 1441 && !this.compareMode) {
      this.maxVisibleLabels = 5;
    }
    if (this.currentBrowserWidth >= 1441 && this.compareMode) {
      this.maxVisibleLabels = 2;
    }
    if (this.currentBrowserWidth >= 1025 && this.currentBrowserWidth < 1440 && this.isLeftNavigationEnabled && !this.compareMode) {
      this.maxVisibleLabels = 1;
    }
    if (this.currentBrowserWidth <= 1024 && !this.compareMode) {
      this.maxVisibleLabels = 1;
    }
    if (this.currentBrowserWidth <= 1024 && this.isLeftNavigationEnabled) {
      this.maxVisibleLabels = 0;
    }
    if (this.currentBrowserWidth <= 768 && !this.isLeftNavigationEnabled) {
      this.maxVisibleLabels = 0;
    }
    if (this.currentBrowserWidth >= 1281 && this.currentBrowserWidth < 1440 && this.compareMode) {
      this.maxVisibleLabels = 1;
    }
    this.cdr.detectChanges();
  }

  private collapseAllCommentsSections(checkins: ReflectionPanel[], assigneeId: string): void {
    const selectedCheckinPanel = checkins.find((reflection) => reflection.assignee.id === assigneeId);

    if (selectedCheckinPanel?.questions) {
      selectedCheckinPanel.questions.forEach((question) => (question.isCommentsEnabled = false));
    }
    selectedCheckinPanel.notes.forEach((note) => (note.isCommentsEnabled = false));
  }

  private toggleComments(checkins: ReflectionPanel[], index: number, data: CheckinCommentsToggle): void {
    checkins[index][data.target][data.commentParentIndex].isCommentsEnabled = data.status;

    if (data?.source === "cancelButton") {
      checkins[index][data.target][data.commentParentIndex].commentPendingText = "";
    }

    checkins[index] = { ...checkins[index] };
    this.reflectionEdit.emit({ checkin: checkins[index] });
  }

  private addComment(checkins: ReflectionPanel[], index: number, data: CheckinCommentChange): void {
    const commentParent = checkins[index][data.target][data.commentParentIndex];
    commentParent.comments.push(data.comment);
    commentParent.commentPendingText = "";

    if (!commentParent.commentsAssignees.find((assignee) => assignee.id === data.comment.createdBy)) {
      commentParent.commentsAssignees.push(this.assignees[data.comment.createdBy]);
    }

    checkins[index] = { ...checkins[index] };
    this.reflectionEdit.emit({ checkin: checkins[index] });
  }

  private removeComment(checkins: ReflectionPanel[], index: number, data: CheckinCommentChange): void {
    const commentParent = checkins[index][data.target][data.commentParentIndex];
    const filteredComments = commentParent.comments.filter((comment) => comment.id !== data.comment.id);
    commentParent.comments = filteredComments;

    if (!commentParent.comments.find((comment) => comment.createdBy === data.comment.createdBy)) {
      const filteredAssignees = commentParent.commentsAssignees.filter((assignee) => assignee.id !== data.comment.createdBy);
      commentParent.commentsAssignees = filteredAssignees;
    }

    checkins[index] = { ...checkins[index] };
    this.reflectionEdit.emit({ checkin: checkins[index] });
  }

  private changeCommentPendingText(checkin: ReflectionPanel, data: CheckinCommentPendingTextChange): void {
    checkin[data.target][data.commentParentIndex].commentPendingText = data.text;
    this.reflectionEdit.emit({ checkin });
  }
}
